import * as React from 'react';
import { gql, useQuery } from '@apollo/client';
import { faBarcodeScan } from '@fortawesome/pro-regular-svg-icons';
import {
  Icon,
  Image,
  Spinner,
  Surface,
  Text,
  useToast,
} from '@quality24/design-system';
import jwtDecode from 'jwt-decode';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  storeAdmissionId,
  storeLoginId,
  storeUserName,
  storeUserType,
} from '@/inpatient-patient-pwa/services/storage';
import LoginProcessForm, {
  LoginProcessFormProps,
} from '@/inpatient-patient-pwa/organisms/LoginProcessForm';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import { useLanguages } from '@/inpatient-patient-pwa/contexts/language/useLanguages';
import LanguageButton from '@/inpatient-patient-pwa/molecules/LanguageButton';
import styles from './LoginPage.module.scss';
import sprite from './sprite.svg';

/**
 * GraphQL Query
 *
 * `pwaLoginHospitalInfo` is a SECURITY DEFINER function exposed to the guest
 * role, so it resolves for the still-unauthenticated device: it takes the PWA
 * token carried by the QR code JWT and returns the hospital's app config subset
 * the login screen needs.
 */
export const HOSPITAL_INFO_QUERY = gql`
  query FetchPwaLoginHospitalInfo($pwaToken: UUID!) {
    pwaLoginHospitalInfo(pwaToken: $pwaToken)
  }
`;

export interface SearchString {
  user?: string;
  pass?: string;
}

interface Token {
  token: string;
  validations?: string[];
}

export interface QueryResult {
  pwaLoginHospitalInfo?: {
    appSupportedLanguages?: Array<string> | null;
  } | null;
}

const LoginPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { error } = useToast();
  const { search } = useLocation();
  const token = React.useMemo(
    () => new URLSearchParams(search).get('token'),
    [search],
  );

  const decodedToken: Token | null = token ? jwtDecode<Token>(token) : null;

  const validations: string[] =
    decodedToken && decodedToken.validations ? decodedToken.validations : [];

  // The login screen is the only place that can read the hospital's language
  // config (the endpoint keys off the QR code's PWA token), so it hands the
  // result to LanguageProvider, which then narrows the whole app to that set.
  const {
    data: hospitalInfo,
    loading,
    error: hospitalInfoError,
  } = useQuery<QueryResult>(HOSPITAL_INFO_QUERY, {
    variables: { pwaToken: decodedToken?.token },
    skip: !decodedToken?.token,
  });

  const { isResolved, setSupportedLanguages } = useLanguages();

  React.useEffect(() => {
    // Only report an actual answer. A failed request says nothing about the
    // hospital's config, so it must not be mistaken for an empty one.
    if (loading || hospitalInfoError || !decodedToken?.token) return;

    setSupportedLanguages(
      hospitalInfo?.pwaLoginHospitalInfo?.appSupportedLanguages,
    );
  }, [
    decodedToken?.token,
    hospitalInfo,
    hospitalInfoError,
    loading,
    setSupportedLanguages,
  ]);

  const { login } = useAuth();
  const navigate = useNavigate();

  const loginPWA = React.useCallback<
    NonNullable<LoginProcessFormProps['onSubmit']>
  >(
    (values) => {
      if (token) {
        login(
          token,
          values.name,
          values.userType,
          values.birthDate,
          values.admissionNumber,
        )
          .then((data) => {
            storeUserType(
              (values.userType as string).toUpperCase() === 'PATIENT_COMPANION'
                ? 'RELATIVE'
                : 'PATIENT',
            );
            storeLoginId(
              data.data?.authFlow?.authenticationResult?.userData?.loginId,
            );
            if (values.name) storeUserName(values.name);

            /**
             * Temporarily store admissionId
             * This might be removed after refreshDeviceTokenWithType is updated in the API to return the admissionId
             */
            storeAdmissionId(
              data.data?.authFlow?.authenticationResult?.userData?.admissionId,
            );

            navigate('/');
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.log('Error on login', err);
            }
            error(t('errors.loadAdmission'));
          });
      }
    },
    [token, login, navigate, error, t],
  );

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>{t('login.pageTitle')}</title>
      </Helmet>

      {token ? (
        <>
          <div className={styles.header}>
            <LanguageButton
              color="primary"
              className={styles['language-button']}
            />

            <Image
              className="w-100"
              src={sprite}
              alt={t('login.illustrationAlt')}
              fluid
            />
          </div>
          <div className={styles.content}>
            {/**
             * First scan on this device: the hospital's languages are still
             * unknown, so hold the form back rather than paint it in a language
             * the hospital may not offer. Later scans read the cached config and
             * render straight away.
             */}
            {loading && !isResolved ? (
              <div className="d-flex justify-content-center p-4">
                <Spinner />
              </div>
            ) : (
              <LoginProcessForm onSubmit={loginPWA} validations={validations} />
            )}
          </div>
        </>
      ) : (
        <div className="w-100 h-100 d-flex flex-column justify-content-center p-4 text-center">
          <Surface className="d-flex flex-column align-items-center justify-content-center gap-3">
            <Icon icon={faBarcodeScan} size="lg" />
            <Text>Por favor, escaneie o QR code fornecido.</Text>
          </Surface>
        </div>
      )}
    </div>
  );
};
export default LoginPage;
